import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, FormGroup, Label, FormText } from 'reactstrap';
import { Field, change, formValueSelector, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import FormItem from '../../../common/forms/FormItem';
import {
  isRequired,
  validateEndDate,
  validateNumber,
} from '../../../../utils/validators';
import LocationSelect from '../../../common/forms/select/LocationSelect';
import TextInput from '../../../common/forms/input/TextInput';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import OrganizationChartSelect from '../../../common/forms/select/OrganizationChartSelect';
import FormFooter from '../../../common/forms/FormFooter';
import Select from '../../../common/forms/select/Select';
import { enumToSelectOptions } from '../../../../utils/enum';
import {
  ACTIVITY_STATUS,
  REGISTER_OF_EXECUTION_TYPE,
} from '../../../../config/constants';
import {
  optionPropTypes,
  optionsPropTypes,
} from '../../../common/forms/select/SelectPropTypes';
import ActivitySelect from '../../../common/forms/select/ActivitySelect';
import { REPORT_REGENERATE_INFO_MESSAGE } from '../../../../config/messages';
import Alert from '../../../common/informative/Alert';

const selector = formValueSelector('PreventiveMaintenanceReportSearchForm');

const validateDate = (value, allValues) =>
  validateEndDate(value, allValues.dateFrom);

const isNotEmpty = (val) => {
  if (val === null || val === undefined) {
    return false;
  }

  if (Array.isArray(val)) {
    return val.length > 0;
  }

  if (typeof val === 'object') {
    return Object.keys(val).length > 0;
  }

  return val !== '';
};

const isEmpty = (val) => !isNotEmpty(val);

const validateBusOrCompanyAreaOrBase = (value, allValues) => {
  const { registeredBusIdList, homeBaseLocationId, companyAreaId } = allValues;

  const hasBus = isNotEmpty(registeredBusIdList);
  const hasBase = isNotEmpty(homeBaseLocationId);
  const hasCompanyArea = isNotEmpty(companyAreaId);

  const total = [hasBus, hasBase, hasCompanyArea].filter(Boolean).length;

  if (total === 0) {
    return 'Debe seleccionar al menos uno: Bus, Base u Organigrama.';
  }

  if (hasBus && (hasBase || hasCompanyArea)) {
    return 'Cuando selecciona Bus, no puede combinar con Base ni Organigrama.';
  }

  if (hasBase && hasCompanyArea && !hasBus) {
    return undefined;
  }

  if (total > 1) {
    return 'Solo puede seleccionar uno, excepto la combinación Organigrama + Base.';
  }

  return undefined;
};

const requireCompanyAreaIfBase = (value, allValues) => {
  const { homeBaseLocationId } = allValues;

  if (isNotEmpty(homeBaseLocationId) && isEmpty(value)) {
    return 'Debe seleccionar un Organigrama cuando ha seleccionado una Base.';
  }

  return undefined;
};

const PreventiveMaintenanceReportSearchForm = ({
  handleSubmit,
  onModal,
  showSimulation,
  showDaily,
  showBase,
  registeredBusIsMulti,
  registeredBusIsRequired,
  loading,
  searchButtonDisabled,
  isForDownload,
  disableDateTo,
  showDateFrom,
  showRegisterOfExecutionType,
  initialValues,
  showActivityField,
  showMaintenanceWarningType,
  registeredBusValue,
  showOrganizationChart,
  organizationCharts,
  loadingOrganizationChart,
  companyAreaIsMulti,
  isProjection,
  homeBaseLocationIdSelected,
  showForceReport,
  dispatchChange,
}) => {
  const [selectedType, setSelectedType] = useState(
    initialValues.registerOfExecutionType
      ? initialValues.registerOfExecutionType.value
      : REGISTER_OF_EXECUTION_TYPE.false.value,
  );

  const [showRadiusButton, setShowRadiusButton] = useState(false);
  const [forceReport, setForceReport] = useState(false);

  useEffect(() => {
    const hasBus = Array.isArray(registeredBusValue)
      ? registeredBusValue.length > 0
      : !!registeredBusValue;

    setShowRadiusButton(hasBus);
  }, [registeredBusValue]);

  useEffect(() => {
    dispatchChange(
      'PreventiveMaintenanceReportSearchForm',
      'companyAreaId',
      null,
    );
  }, [homeBaseLocationIdSelected, dispatchChange]);

  let formProps = { onSubmit: handleSubmit };
  let buttonProps = {
    saveButtonAction: null,
    saveButtonColor: 'secondary',
    saveButtonDisabled: loading || searchButtonDisabled,
    saveButtonIcon: 'fa fa-search',
    saveButtonType: 'submit',
    saveButtonText: 'Buscar',
  };

  const previousButtonProps = { ...buttonProps };

  if (onModal) {
    formProps = null;
    buttonProps = {
      ...previousButtonProps,
      saveButtonType: 'button',
      saveButtonAction: handleSubmit,
    };
  }

  if (isForDownload) {
    buttonProps = {
      ...previousButtonProps,
      saveButtonText: 'Generar PDF',
      saveButtonIcon: 'fa fa-file-pdf-o',
      saveButtonColor: 'primary',
    };
  }

  if (loading || searchButtonDisabled) {
    buttonProps = {
      ...previousButtonProps,
      saveButtonIcon: 'fa fa-spinner fa-spin fa-fw',
      saveButtonText: 'Cargando...',
    };
  }

  const dailyField = showDaily && (
    <FormGroup row>
      <FormItem>
        <Label>
          <Field name="daily" component="input" type="checkbox" /> Diario
        </Label>
      </FormItem>
    </FormGroup>
  );

  const simulationFields = showSimulation && (
    <FormGroup row>
      <FormItem label="Simulación">
        <Field
          name="simulationKilometers"
          component={TextInput}
          type="text"
          id="simulationKilometers"
          showToolTip
          placeholder="Simulación en Kilómetros"
          validate={[validateNumber]}
        />
      </FormItem>
    </FormGroup>
  );

  const activityField = showActivityField && (
    <FormGroup row>
      <FormItem label="Actividad">
        <Field name="activityId" component={ActivitySelect} />
      </FormItem>
    </FormGroup>
  );

  const getCompanyAreaValidation = () => {
    const validations = [];

    if (!isProjection) {
      validations.push(companyAreaIsMulti ? isRequired : isRequired);
    }

    if (isProjection) {
      validations.push(validateBusOrCompanyAreaOrBase);
    }

    validations.push(requireCompanyAreaIfBase);

    return validations;
  };

  const forceAlertMessage = forceReport && (
    <Alert type="warning" message={REPORT_REGENERATE_INFO_MESSAGE} />
  );

  const onSelectAll = (checked) => {
    let companyAreaIdValue = null;

    if (checked) {
      companyAreaIdValue = organizationCharts;
    }

    dispatchChange(
      'PreventiveMaintenanceReportSearchForm',
      'companyAreaId',
      companyAreaIdValue,
    );
  };

  const organizationChartField = (showOrganizationChart ||
    homeBaseLocationIdSelected) && (
    <>
      <FormGroup row>
        <FormItem
          label={
            homeBaseLocationIdSelected ? 'Organigrama (todos)' : 'Organigrama'
          }
          required={!isProjection || !!homeBaseLocationIdSelected}
        >
          <Field
            name="companyAreaId"
            component={OrganizationChartSelect}
            isClearable
            isMulti={companyAreaIsMulti}
            {...(homeBaseLocationIdSelected ? {} : { useByUser: true })}
            placeholder={
              homeBaseLocationIdSelected
                ? 'Seleccione... (todos los organigramas)'
                : 'Seleccione...'
            }
            validate={getCompanyAreaValidation()}
          />

          {companyAreaIsMulti && (
            <Label>
              <Field
                name="selectAll"
                component="input"
                type="checkbox"
                onChange={(e) => onSelectAll(e.target.checked)}
                disabled={loadingOrganizationChart}
              />{' '}
              Seleccionar todos
            </Label>
          )}
        </FormItem>
      </FormGroup>

      {showForceReport && (
        <FormGroup row>
          <FormItem label="">
            <Label>
              <Field
                name="force"
                component="input"
                type="checkbox"
                onChange={(e) => setForceReport(e.target.checked)}
              />{' '}
              Forzar la generación del reporte
            </Label>
            {forceAlertMessage}
          </FormItem>
        </FormGroup>
      )}
    </>
  );

  const registerOfExecutionTypeMessage =
    selectedType === REGISTER_OF_EXECUTION_TYPE.true.value ? (
      <div>Mostrará todas las actividades sin importar el estado.</div>
    ) : (
      <div>
        Mostrará sólo las actividades con estado {ACTIVITY_STATUS.PENDING.label}
        .
      </div>
    );

  const maintenanceTypeField = showRegisterOfExecutionType && (
    <FormGroup row>
      <FormItem label="Tipo de Registro" required>
        <Field
          name="registerOfExecutionType"
          options={enumToSelectOptions(REGISTER_OF_EXECUTION_TYPE)}
          component={Select}
          onChange={(option) => setSelectedType(option.value)}
          validate={[isRequired]}
          isClearable={false}
          isDisabled
        />
        <FormText>{registerOfExecutionTypeMessage}</FormText>
      </FormItem>
    </FormGroup>
  );

  const busAndBaseLabel = showBase ? 'Bus/Base' : 'Bus';

  const busField = (
    <Field
      name="registeredBusIdList"
      id="registeredBusIdList"
      component={RegisteredBusSelect}
      isMulti={registeredBusIsMulti}
      validate={[validateBusOrCompanyAreaOrBase]}
      autoFocus
      placeholder="Bus"
      showToolTip
    />
  );

  const busAndBaseField = showBase ? (
    <Row>
      <Col>{busField}</Col>
      <Col>
        <Field
          name="homeBaseLocationId"
          id="homeBaseLocationId"
          component={LocationSelect}
          forHomeBase
          validate={[validateBusOrCompanyAreaOrBase]}
          placeholder="Base"
          showToolTip
          isClearable
        />
      </Col>
    </Row>
  ) : (
    <Row>
      <Col>{busField}</Col>
    </Row>
  );

  const dateToValidations = [isRequired];

  if (showDateFrom) {
    dateToValidations.push(validateDate);
  }

  const dateToField = (
    <Field
      name="dateTo"
      id="dateTo"
      component={TextInput}
      type="date"
      validate={dateToValidations}
      disabled={disableDateTo}
      showToolTip
      placeholder={showDateFrom ? 'Fecha Final' : 'Fecha'}
    />
  );

  const dateFromAndToField = showDateFrom ? (
    <Row>
      <Col>
        <Field
          name="dateFrom"
          id="dateFrom"
          component={TextInput}
          type="date"
          validate={[isRequired]}
          disabled={disableDateTo}
          showToolTip
          placeholder="Fecha Inicial"
        />
      </Col>
      <Col>{dateToField}</Col>
    </Row>
  ) : (
    dateToField
  );

  return (
    <Form {...formProps}>
      {dailyField}

      <FormGroup row>
        <FormItem label={busAndBaseLabel} required={registeredBusIsRequired}>
          {busAndBaseField}
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label={showDateFrom ? 'Fechas' : 'Fecha'} required>
          {dateFromAndToField}
        </FormItem>
      </FormGroup>

      {organizationChartField}
      {activityField}
      {simulationFields}
      {maintenanceTypeField}

      {showRadiusButton && showMaintenanceWarningType && (
        <FormGroup row>
          <FormItem label="Tipo de Ocurrencias">
            <div>
              <Label className="mr-3">
                <Field
                  name="warningType"
                  component="input"
                  type="radio"
                  value="both"
                />{' '}
                Preventivo y Correctivo
              </Label>

              <Label className="mr-3">
                <Field
                  name="warningType"
                  component="input"
                  type="radio"
                  value="preventive"
                />{' '}
                Preventivo
              </Label>

              <Label>
                <Field
                  name="warningType"
                  component="input"
                  type="radio"
                  value="corrective"
                />{' '}
                Correctivo
              </Label>
            </div>
          </FormItem>
        </FormGroup>
      )}

      <FormFooter {...buttonProps} />
    </Form>
  );
};

PreventiveMaintenanceReportSearchForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    dateTo: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    registerOfExecutionType: optionPropTypes,
  }),
  onModal: PropTypes.bool,
  showSimulation: PropTypes.bool,
  showDaily: PropTypes.bool,
  showBase: PropTypes.bool,
  registeredBusIsMulti: PropTypes.bool,
  registeredBusIsRequired: PropTypes.bool,
  searchButtonDisabled: PropTypes.bool,
  isForDownload: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  disableDateTo: PropTypes.bool,
  showDateFrom: PropTypes.bool,
  showRegisterOfExecutionType: PropTypes.bool,
  showActivityField: PropTypes.bool,
  showMaintenanceWarningType: PropTypes.bool,
  registeredBusValue: PropTypes.instanceOf(Array),
  showOrganizationChart: PropTypes.bool,
  organizationCharts: optionsPropTypes,
  loadingOrganizationChart: PropTypes.bool.isRequired,
  companyAreaIsMulti: PropTypes.bool,
  isProjection: PropTypes.bool,
  homeBaseLocationIdSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  showForceReport: PropTypes.bool,
  dispatchChange: PropTypes.func.isRequired,
};

PreventiveMaintenanceReportSearchForm.defaultProps = {
  initialValues: { dateTo: null },
  onModal: false,
  showSimulation: false,
  showDaily: false,
  showBase: false,
  registeredBusIsMulti: false,
  registeredBusIsRequired: false,
  searchButtonDisabled: false,
  isForDownload: false,
  disableDateTo: false,
  showDateFrom: false,
  showRegisterOfExecutionType: REGISTER_OF_EXECUTION_TYPE.false.value,
  showActivityField: false,
  showMaintenanceWarningType: false,
  registeredBusValue: [],
  showOrganizationChart: false,
  organizationCharts: [],
  companyAreaIsMulti: false,
  isProjection: false,
  homeBaseLocationIdSelected: null,
  showForceReport: false,
};

const mapStateToProps = (state) => ({
  loading: state.MechanicalMaintenanceUnit.PreventiveMaintenanceReport.getIn([
    'all',
    'loading',
  ]),
  organizationCharts: state.MechanicalMaintenanceUnit.OrganizationChart.getIn([
    'all',
    'content',
    'content',
  ]).map(({ id, name, companyAreaType }) => ({
    value: id,
    label: `${name} (${companyAreaType.name})`,
    name,
  })),
  loadingOrganizationChart:
    state.MechanicalMaintenanceUnit.OrganizationChart.getIn(['all', 'loading']),
  homeBaseLocationIdSelected: selector(state, 'homeBaseLocationId'),
  registeredBusValue: selector(state, 'registeredBusIdList'),
});

const mapDispatchToProps = {
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: 'PreventiveMaintenanceReportSearchForm',
  enableReinitialize: false,
})(PreventiveMaintenanceReportSearchForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
